import { Graphics } from "@/components/graphics";
import { Locales } from "@/lib/i18n";
import { getSingle } from "@/lib/prismic";
import { cn } from "@/lib/utils";
import { Content } from "@prismicio/client";
import Image from "next/image";

type OurServicesProps = {
  className?: string;
  locale: Locales;
};

const OurServices = async ({ className, locale }: OurServicesProps) => {
  const servicesData = await getSingle<Content.SiteSettingsDocument>("siteSettings", locale);

  if (!servicesData?.data) return null;

  const title = servicesData.data.reassurance_title;

  const lastWordOfTitle = title?.split(" ").pop() || "";
  const titleWithoutLastWord = title?.slice(0, title?.lastIndexOf(" "));

  return (
    <section
      aria-labelledby="our-services"
      className={cn(
        "from-pink-primary/6 font-cabinet-grotesk text-dark-primary mx-4 my-5 rounded-lg bg-gradient-to-b to-white pt-16 pb-28 md:mx-7.5",
        className
      )}
    >
      <div className="mx-auto max-w-[1000px]">
        {title && (
          <h2 id="our-services" className="mx-9 text-center text-2xl font-medium sm:text-3xl">
            {titleWithoutLastWord}{" "}
            <mark className="relative bg-transparent font-bold text-pink-500">
              {lastWordOfTitle}
              <Graphics.highlight className="absolute -bottom-0.5 left-0 h-2 w-full" />
            </mark>
          </h2>
        )}
        {servicesData.data.reassurance_description && (
          <p className="text-primary-light mx-9 mt-2.5 text-center text-sm">
            {servicesData.data.reassurance_description}{" "}
          </p>
        )}
        <ul className="mt-12.5 grid w-full grid-cols-1 gap-8 place-items-start overflow-hidden md:grid-cols-2 lg:grid-cols-4">
          {servicesData.data.reassurance_elements &&
            servicesData.data.reassurance_elements.length >= 1 &&
            servicesData.data.reassurance_elements.map((element, index) => (
              <li key={index} className="relative flex w-full flex-col items-center justify-center text-center">
                <div className="relative flex items-center h-16 md:translate-x-[40%] justify-center">
                  <figure className="relative z-10 flex items-center justify-center">
                    <Image
                      src={element.icon.url || ""}
                      alt={element.icon.alt || ""}
                      width={56}
                      height={56}
                      className="mix-blend-multiply"
                    />
                  </figure>
                  <hr
                    className={cn(
                      "hidden w-32 mx-4 lg:block",
                      index < servicesData.data.reassurance_elements.length - 1
                        ? "border-primary-light/50"
                        : "border-transparent"
                    )}
                  />
                </div>

                <div className="mt-4 space-y-3">
                  <h3 className="text-xl font-medium">{element.title}</h3>
                  <p className="text-primary-light text-sm">{element.description}</p>
                </div>
              </li>
            ))}
        </ul>
      </div>
    </section>
  );
};

export default OurServices;
